import classNames from 'classnames';
import { isBoolean, isEmpty, isNil, omitBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import ReactJson from 'react-json-view';
import { Link, useHistory, useLocation } from 'react-router-dom';

import ErrorModal from '../../app/modals/ErrorModal';
import { IPropsEbookBundleItem } from '../../app/sections/components/EbookBundleSectionItem';
import { IPropsEbookItem } from '../../app/sections/components/EbookSectionItem';
import { IPropsEditorReviewItem } from '../../app/sections/components/EditorReviewSectionItem';
import { IPropsFeedItem } from '../../app/sections/components/FeedSectionItem';
import { IPropsLinkItem } from '../../app/sections/components/LinkSectionItem';
import { IPropsPintoCustomLinkItem } from '../../app/sections/components/PintoCustomLinkSectionItem';
import { IPropsProductSetItem } from '../../app/sections/components/ProductSetSectionItem';
import { IPropsQueryEbookBundleItem } from '../../app/sections/components/QueryEbookBundleSectionItem';
import { IPropsQueryEbookItem } from '../../app/sections/components/QueryEbookSectionItem';
import { IPropsQueryProductSetItem } from '../../app/sections/components/QueryProductSetSectionItem';
import { IPropsSearchItem } from '../../app/sections/components/SearchSectionItem';
import { IPropsShortcutItem } from '../../app/sections/components/ShortcutSectionItem';
import AddItemInSectionPresenter from '../../app/sections/presenters/AddItemInSectionPresenter';
import BookCoverWithBannerSectionDetailPresenter from '../../app/sections/presenters/BookCoverWithBannerSectionDetailPresenter';
import SectionDetailPresenter from '../../app/sections/presenters/SectionDetailPresenter';
import SectionDetailWithTargetPresenter from '../../app/sections/presenters/SectionDetailWithTargetPresenter';
import SectionNotePresenter from '../../app/sections/presenters/SectionNotePresenter';
import SelectLayoutSectionPresenter from '../../app/sections/presenters/SelectLayoutSectionPresenter';
import SectionBuilderFormObj, {
  EXPLORE_TYPE_VALUE,
  IConfigItemType,
  ISectionBuilderFields,
  ISectionLayoutId,
  ISectionTargetType,
  PINTO_SHORTCUT_TYPE_MAP,
  PINTO_SHORTCUT_TYPE_MAP_REVERSE,
  SECTION_BUILDER_FIELD_KEYS,
} from '../../app/sections/validates/sectionBuilderSchema';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingModal from '../../components/modal/LoadingModal';
import {
  ContentType,
  Explore,
  FeedPref,
  PintoCategory,
  ProductType,
  SectionLayoutId,
  SectionTargetExploreEbookBundle,
  SectionTargetExploreEbookBundleType,
} from '../../core/graphql/types';
import useMutationPintoCreateSection from '../../hooks/useMutationPintoCreateSection';
import useMutationPintoUpdateSection from '../../hooks/useMutationPintoUpdateSection';
import useQueryPintoCategories from '../../hooks/useQueryPintoCategories';
import useQueryPintoSection from '../../hooks/useQueryPintoSection';
import { findCategoryById } from '../../utils/categoryUtils';
import useHookForm from '../../utils/form/useHookForm';
import { camelToSnakeCase } from '../../utils/string';

type IPropsConfigItem =
  | IPropsEbookItem
  | IPropsFeedItem
  | IPropsLinkItem
  | IPropsPintoCustomLinkItem
  | IPropsQueryEbookItem
  | IPropsQueryEbookBundleItem
  | IPropsSearchItem
  | IPropsProductSetItem
  | IPropsQueryProductSetItem
  | IPropsEbookBundleItem
  | IPropsEditorReviewItem
  | IPropsShortcutItem;

const MAPPED_TARGET_TYPE: { [key: string]: string } = {
  Feed: 'feed',
  Link: 'link',
  User: 'user',
  Explore: 'explore',
  SectionTargetExploreEbookBundle: 'explore-ebook-bundle',
  SearchLink: 'search',
  ExploreProductSet: 'explore-product-set',
};

export const onGetTargetTypeData = (targetType?: string) => {
  if (!targetType) return null;
  return MAPPED_TARGET_TYPE[targetType] || null;
};

const MAX_SHORTCUT_ITEMS = 9;

const getShortcutConfigItem = (configItem: { label: string; dayImageUrl: string; darkImageUrl: string }) => ({
  label: configItem.label,
  dayImageUrl: configItem.dayImageUrl,
  darkImageUrl: configItem.darkImageUrl,
});

const parseNullableBooleanString = (value?: string | boolean | null) => {
  if (value === null || value === undefined || value === 'null') return null;
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const stringifyNullableBoolean = (value?: boolean | null) => {
  if (value === null || value === undefined) return undefined;
  return String(value);
};

const normalizeCategoryId = (categoryId?: string | number | null) => {
  if (categoryId === null || categoryId === undefined || categoryId === '') return undefined;
  const normalizedCategoryId = Number(categoryId);
  if (Number.isNaN(normalizedCategoryId) || normalizedCategoryId === 0) return undefined;
  return normalizedCategoryId;
};

const getComicWritingTypes = (allCategories: PintoCategory[], categoryId?: string | number | null, anyWritingTypes?: unknown[] | null) => {
  const normalizedCategoryId = normalizeCategoryId(categoryId);

  if (!normalizedCategoryId || isEmpty(anyWritingTypes)) {
    return null;
  }

  return findCategoryById(allCategories, normalizedCategoryId)?.[0].contentType !== ContentType.Comic ? null : anyWritingTypes;
};

const shouldHydrateAllCategoryForTarget = (type?: string | null, target?: Record<string, unknown> | null) => {
  if (!target) return false;
  if (![ISectionTargetType.SEARCH, ISectionTargetType.EXPLORE, ISectionTargetType.PRODUCT_SET].includes(type as ISectionTargetType)) return false;
  if (type === ISectionTargetType.SEARCH && target.searchType === 'user') return false;
  return target.categoryId === null || target.categoryId === undefined || target.categoryId === '';
};

const shouldHydrateAllCategoryForConfigItem = (item?: Record<string, unknown> | null) => {
  if (!item) return false;

  const itemType = item.type;
  const usesCategorySelector =
    itemType === IConfigItemType.QueryEbook ||
    itemType === IConfigItemType.QueryProductSet ||
    itemType === IConfigItemType.ExploreShortcut ||
    (itemType === IConfigItemType.Search && item.searchType === 'ebook');

  if (!usesCategorySelector) return false;
  return item.categoryId === null || item.categoryId === undefined || item.categoryId === '';
};

export const onConvertDataToConfigItems = (configItem: IPropsConfigItem, allCategories: PintoCategory[]) => {
  switch (configItem.type) {
    case IConfigItemType.Ebook:
    case IConfigItemType.EbookShortcut: {
      const tempConfigItem: Record<string, unknown> = {
        // require
        type: configItem.type,
        _id: configItem._id,
      };

      if (configItem.type === IConfigItemType.EbookShortcut) Object.assign(tempConfigItem, getShortcutConfigItem(configItem));

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.EbookBundle:
    case IConfigItemType.EbookBundleShortcut: {
      const tempConfigItem: Record<string, unknown> = {
        // require
        type: configItem.type,
        _id: configItem._id,
      };

      if (configItem.type === IConfigItemType.EbookBundleShortcut) Object.assign(tempConfigItem, getShortcutConfigItem(configItem));

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.EbookCollectionShortcut: {
      const tempConfigItem: Record<string, unknown> = { type: configItem.type, _id: configItem._id };
      Object.assign(tempConfigItem, getShortcutConfigItem(configItem));

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.Feed:
    case IConfigItemType.PintoFeedShortcut: {
      const tempConfigItem: Record<string, unknown> = {
        // require
        type: configItem.type,
        platform: 'pinto',
        // optional
        slug: isEmpty(configItem.slug) ? null : configItem?.slug,
        title: isEmpty(configItem.title) ? null : configItem?.title,
        description: isEmpty(configItem.description) ? null : configItem?.description,
        coverImage: isEmpty(configItem.coverImage) ? null : configItem?.coverImage,
        bannerImage: isEmpty(configItem.bannerImage) ? null : configItem?.bannerImage,
        popupImageUrl: isEmpty(configItem.popupImageUrl) ? null : configItem?.popupImageUrl,
      };

      if (configItem.type === IConfigItemType.PintoFeedShortcut) {
        Object.assign(tempConfigItem, getShortcutConfigItem(configItem));
        delete tempConfigItem.platform; // Remove platform for shortcuts
      }

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.Link:
    case IConfigItemType.LinkShortcut: {
      const tempConfigItem: Record<string, unknown> = {
        // require
        type: configItem.type,
        url: configItem.url,
        // optional
        title: isEmpty(configItem.title) ? null : configItem?.title,
        description: isEmpty(configItem.description) ? null : configItem?.description,
        coverImage: isEmpty(configItem.coverImage) ? null : configItem?.coverImage,
        bannerImage: isEmpty(configItem.bannerImage) ? null : configItem?.bannerImage,
        popupImageUrl: isEmpty(configItem.popupImageUrl) ? null : configItem?.popupImageUrl,
      };

      if (configItem.type === IConfigItemType.LinkShortcut) Object.assign(tempConfigItem, getShortcutConfigItem(configItem));

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.PintoCustomLink: {
      const tempConfigItem: Record<string, unknown> = {
        // require
        type: configItem.type,
        link: configItem.link,
        deeplink: isEmpty(configItem.deeplink) ? null : configItem?.deeplink,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Object.assign(tempConfigItem, getShortcutConfigItem(configItem as any));

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.QueryEbook: {
      const tempConfigItem = {
        // require
        type: configItem.type,
        categoryId: normalizeCategoryId(configItem.categoryId),
        // optional
        gteRating: Number(configItem.gteRating) ? Number(configItem.gteRating) : null,
        hasDiscount: isBoolean(configItem.hasDiscount) ? configItem.hasDiscount : null,
        gteDiscountRatio: Number(configItem.gteDiscountRatio) ? Number(configItem.gteDiscountRatio) / 100 : null,
        lteDiscountRatio: Number(configItem.lteDiscountRatio) ? Number(configItem.lteDiscountRatio) / 100 : null,
        anyTypes: isEmpty(configItem.anyTypes) ? null : configItem.anyTypes,
        anyUserIds: isEmpty(configItem.anyUserIds) ? null : configItem.anyUserIds,
        blacklistUserIds: isEmpty(configItem.blacklistUserIds) ? null : configItem.blacklistUserIds,
        whitelistIds: isEmpty(configItem.whitelistIds) ? null : configItem.whitelistIds,
        blacklistIds: isEmpty(configItem.blacklistIds) ? null : configItem.blacklistIds,
        tagNames: isEmpty(configItem.tagNames) ? null : configItem.tagNames,
        anyTagNames: isEmpty(configItem.anyTagNames) ? null : configItem.anyTagNames,
        isFree: isBoolean(configItem.isFree) ? configItem.isFree : null,
        sortType: isEmpty(configItem.sortType) ? null : configItem.sortType,
        sortBy: isEmpty(configItem.sortBy) ? null : configItem.sortBy,
        limit: Number(configItem.limit) ? Number(configItem.limit) : null,
        anyWritingTypes: getComicWritingTypes(allCategories, configItem.categoryId, configItem.anyWritingTypes),
      };
      return omitBy(tempConfigItem, isNil);
    }
    case IConfigItemType.QueryEbookBundle: {
      const tempConfigItem = {
        // require
        type: configItem.type,
        // optional
        categoryId: normalizeCategoryId(configItem.categoryId),
        hasDiscount: isBoolean(configItem.hasDiscount) ? configItem.hasDiscount : null,
        hasDiscountEnded: parseNullableBooleanString(configItem.hasDiscountEnded),
        gteDiscountRatio: Number(configItem.gteDiscountRatio) ? Number(configItem.gteDiscountRatio) / 100 : null,
        lteDiscountRatio: Number(configItem.lteDiscountRatio) ? Number(configItem.lteDiscountRatio) / 100 : null,
        anyUserIds: isEmpty(configItem.anyUserIds) ? null : configItem.anyUserIds,
        whitelistIds: isEmpty(configItem.whitelistIds) ? null : configItem.whitelistIds,
        blacklistIds: isEmpty(configItem.blacklistIds) ? null : configItem.blacklistIds,
        tagNames: isEmpty(configItem.tagNames) ? null : configItem.tagNames,
        anyTagNames: isEmpty(configItem.anyTagNames) ? null : configItem.anyTagNames,
        sortType: isEmpty(configItem.sortType) ? null : configItem.sortType,
        sortBy: isEmpty(configItem.sortBy) ? null : configItem.sortBy,
        limit: Number(configItem.limit) ? Number(configItem.limit) : null,
      };
      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.Search:
    case IConfigItemType.SearchShortcut: {
      let result: Record<string, unknown> = {};

      if (configItem?.searchType === 'user') {
        result = {
          // require
          type: configItem.type,
          searchType: 'user',
          // optional
          searchText: isEmpty(configItem.searchText) ? null : configItem?.searchText,
          title: isEmpty(configItem.title) ? null : configItem?.title,
          description: isEmpty(configItem.description) ? null : configItem?.description,
          coverImage: isEmpty(configItem.coverImage) ? null : configItem?.coverImage,
          bannerImage: isEmpty(configItem.bannerImage) ? null : configItem?.bannerImage,
          popupImageUrl: isEmpty(configItem.popupImageUrl) ? null : configItem?.popupImageUrl,
        };
      }

      if (configItem?.searchType === 'ebook') {
        result = {
          // require
          type: configItem.type,
          searchType: 'ebook',
          sortBy: configItem.sortBy,
          sortType: configItem.sortType,
          categoryId: String(configItem.categoryId) === '0' ? 0 : normalizeCategoryId(configItem.categoryId),
          // optional
          searchText: isEmpty(configItem.searchText) ? null : configItem?.searchText,
          title: isEmpty(configItem.title) ? null : configItem?.title,
          description: isEmpty(configItem.description) ? null : configItem?.description,
          coverImage: isEmpty(configItem.coverImage) ? null : configItem?.coverImage,
          bannerImage: isEmpty(configItem.bannerImage) ? null : configItem?.bannerImage,
          popupImageUrl: isEmpty(configItem.popupImageUrl) ? null : configItem?.popupImageUrl,
          searchField: isEmpty(configItem.searchField) ? null : configItem?.searchField,
          gteRating: Number(configItem.gteRating) ? Number(configItem.gteRating) : null,
          anyPriceTypes: isEmpty(configItem.anyPriceTypes) ? null : configItem.anyPriceTypes,
          anyTypes: isEmpty(configItem.anyTypes) ? null : configItem.anyTypes,
          anyWritingTypes: getComicWritingTypes(allCategories, configItem.categoryId, configItem.anyWritingTypes),
        };
      }

      if (configItem.type === IConfigItemType.SearchShortcut) Object.assign(result, getShortcutConfigItem(configItem));

      return omitBy(result, isNil);
    }

    case IConfigItemType.ProductSet:
    case IConfigItemType.ProductSetShortcut: {
      const tempConfigItem: Record<string, unknown> = {
        // require
        type: configItem.type,
        _id: configItem._id,
      };

      if (configItem.type === IConfigItemType.ProductSetShortcut) Object.assign(tempConfigItem, getShortcutConfigItem(configItem));

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.QueryProductSet: {
      const tempConfigItem = {
        // require
        type: configItem.type,
        sortType: isEmpty(configItem.sortType) ? null : configItem.sortType,
        sortBy: isEmpty(configItem.sortBy) ? null : configItem.sortBy,
        limit: Number(configItem.limit) ? Number(configItem.limit) : null,
        categoryId: normalizeCategoryId(configItem.categoryId),
        anyUserIds: isEmpty(configItem.anyUserIds) ? null : configItem.anyUserIds,
        whitelistIds: isEmpty(configItem.whitelistIds) ? null : configItem.whitelistIds,
        blacklistIds: isEmpty(configItem.blacklistIds) ? null : configItem.blacklistIds,
      };

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.EditorReviewEbook: {
      const tempConfigItem = {
        // require
        type: configItem.type,
        _id: configItem._id, // ebookId
        review: isEmpty(configItem.review) ? null : configItem?.review,
        reviewBy: isEmpty(configItem.reviewBy) ? null : configItem?.reviewBy,
      };

      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.ExploreShortcut: {
      const tempConfigItem: Record<string, unknown> = {
        type: configItem.type,
        exploreType: configItem.exploreType ?? EXPLORE_TYPE_VALUE.Trending,
        categoryId: normalizeCategoryId(configItem.categoryId),
        sortBy: configItem.sortBy ?? null,
        sortType: configItem.sortType ?? null,
        gteRating: configItem.gteRating ? Number(configItem.gteRating) : null,
        anyPriceTypes: isEmpty(configItem.anyPriceTypes) ? undefined : configItem.anyPriceTypes,
        anyTypes: isEmpty(configItem.anyTypes) ? undefined : configItem.anyTypes,
        anyFileTypes: isEmpty(configItem.anyFileTypes) ? undefined : configItem.anyFileTypes,
        anyWritingTypes: getComicWritingTypes(allCategories, configItem.categoryId, configItem.anyWritingTypes) ?? undefined,
      };

      Object.assign(tempConfigItem, getShortcutConfigItem(configItem));
      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.ExploreBundleShortcut: {
      const tempConfigItem: Record<string, unknown> = {
        type: configItem.type,
        exploreType: configItem.exploreType || SectionTargetExploreEbookBundleType.BestSeller,
        categoryId: normalizeCategoryId(configItem.categoryId),
        sortBy: configItem.sortBy || null,
        sortType: configItem.sortType || null,
        anyPriceTypes: isEmpty(configItem.anyPriceTypes) ? null : configItem.anyPriceTypes,
      };

      Object.assign(tempConfigItem, getShortcutConfigItem(configItem));
      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.ExploreEbookCollectionShortcut: {
      // const rawIds = configItem.whitelistUserIds ?? [];
      // const deduped = uniq(compact(rawIds));
      const tempConfigItem: Record<string, unknown> = {
        type: configItem.type,
        whitelistUserIds: null, //deduped.length > 0 ? deduped : null, // ยังไม่ใช้ Edge case เยอะแต่รองรับไว้หมดแล้ว
      };

      Object.assign(tempConfigItem, getShortcutConfigItem(configItem));
      return omitBy(tempConfigItem, isNil);
    }

    case IConfigItemType.UserProfileShortcut: {
      const tempConfigItem: Record<string, unknown> = {
        type: configItem.type,
        _id: configItem._id,
        tab: configItem.tab || null,
      };

      Object.assign(tempConfigItem, getShortcutConfigItem(configItem));
      return omitBy(tempConfigItem, isNil);
    }
  }
};

const SectionBuilderPage = () => {
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const searchSlug = searchParams.get('id');

  const { pintoCategories: allCategories } = useQueryPintoCategories();
  const { onQueryPintoSection, dataPintoSection, isLoadingPintoSection } = useQueryPintoSection();
  const { onMutatePintoCreateSection, isLoadingMutatePintoCreateSection } = useMutationPintoCreateSection();
  const { onMutatePintoUpdateSection, isLoadingMutatePintoUpdateSection } = useMutationPintoUpdateSection();

  const { methods, setValue, watch } = useHookForm<ISectionBuilderFields>({
    objectShapeForm: SectionBuilderFormObj,
    defaultValues: {
      gradientMode: false,
      textLightColorHexcode: '#ffffff',
      textDarkColorHexcode: '#ffffff',
      bgLightColorHexcode1: '#ff0000',
      bgDarkColorHexcode1: '#ff0000',
      bgLightColorHexcode2: '#5c21a8',
      bgDarkColorHexcode2: '#2d0060',
    },
  });
  const { remove } = useFieldArray({
    control: methods.control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'configItems', // unique name for your Field Array
  });
  const [title, target, layoutId, configItems, note, contentType] = watch(['title', 'target', 'layoutId', 'configItems', 'note', 'contentType']);
  const [
    announcementText,
    textLightColorHexcode,
    textDarkColorHexcode,
    bgLightColorHexcode1,
    bgDarkColorHexcode1,
    bgLightColorHexcode2,
    bgDarkColorHexcode2,
    gradientMode,
    sectionImageUrl,
    bannerFullWidthImageUrl,
    dayBgImageUrl,
    darkBgImageUrl,
  ] = watch([
    'announcementText',
    'textLightColorHexcode',
    'textDarkColorHexcode',
    'bgLightColorHexcode1',
    'bgDarkColorHexcode1',
    'bgLightColorHexcode2',
    'bgDarkColorHexcode2',
    'gradientMode',
    'imageUrl',
    'bannerFullWidthImageUrl',
    'dayBgImageUrl',
    'darkBgImageUrl',
  ]);

  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string[]>(['กรุณาตรวจสอบความถูกต้อง แล้วลองบันทึกอีกรอบ']);

  const validateTarget = useCallback(() => {
    switch (target?.type) {
      case ISectionTargetType.PRODUCT_SET:
      case ISectionTargetType.EXPLORE:
        return isEmpty(target?.categoryId) && Number(target?.categoryId) !== 0;
      default:
        return false;
    }
  }, [target?.categoryId, target?.type]);

  const isEnableSubmitButton = useMemo(() => {
    if (layoutId === ISectionLayoutId.MainBanner || layoutId === ISectionLayoutId.TagCloud) {
      return isEmpty(note) || isEmpty(layoutId);
    } else if (layoutId === ISectionLayoutId.MarqueeAnnouncement) {
      return isEmpty(note) || isEmpty(layoutId) || isEmpty(announcementText);
    } else if (layoutId === ISectionLayoutId.BookCoverWithBanner) {
      return isEmpty(note) || isEmpty(title) || isEmpty(layoutId) || isEmpty(sectionImageUrl) || validateTarget();
    } else if (layoutId === ISectionLayoutId.BannerFullWidth) {
      return isEmpty(note) || isEmpty(layoutId) || isEmpty(bannerFullWidthImageUrl);
    } else {
      return isEmpty(note) || isEmpty(title) || isEmpty(layoutId) || validateTarget();
    }
  }, [announcementText, bannerFullWidthImageUrl, layoutId, note, sectionImageUrl, title, validateTarget]);

  useEffect(() => {
    if (searchSlug) {
      onQueryPintoSection(searchSlug);
    }
  }, [onQueryPintoSection, searchSlug]);

  useEffect(() => {
    setValue?.('target', null);
  }, [setValue]);

  useEffect(() => {
    if (dataPintoSection) {
      setValue(SECTION_BUILDER_FIELD_KEYS.TITLE, dataPintoSection.pintoSection?.title as never);

      if (dataPintoSection.pintoSection?.target) {
        const type = onGetTargetTypeData(dataPintoSection.pintoSection?.target?.__typename);
        const targetData = dataPintoSection.pintoSection?.target as Record<string, unknown>;
        setValue(SECTION_BUILDER_FIELD_KEYS.TARGET, {
          ...targetData,
          ...(shouldHydrateAllCategoryForTarget(type, targetData) ? { categoryId: 0 } : {}),
          type,
          sortBy:
            type === ISectionTargetType.EXPLORE_BUNDLES
              ? (dataPintoSection.pintoSection?.target as SectionTargetExploreEbookBundle & { bundleSortBy: string })?.bundleSortBy
              : (dataPintoSection.pintoSection?.target as Explore)?.sortBy,
          exploreType:
            type === ISectionTargetType.EXPLORE_BUNDLES
              ? (dataPintoSection.pintoSection?.target as SectionTargetExploreEbookBundle & { bundleExploreType: string })?.bundleExploreType
              : (dataPintoSection.pintoSection?.target as Explore)?.exploreType,
          anyPriceTypes: (dataPintoSection.pintoSection?.target as SectionTargetExploreEbookBundle)?.hasDiscount ? ['onSale'] : [],
        } as never);
      } else {
        setValue(SECTION_BUILDER_FIELD_KEYS.TARGET, null as never);
      }

      setValue(
        SECTION_BUILDER_FIELD_KEYS.LAYOUT_ID,
        dataPintoSection.pintoSection?.camelCaseLayoutId === 'bookCover2'
          ? ('book_cover_2' as never)
          : dataPintoSection.pintoSection?.camelCaseLayoutId === SectionLayoutId.BannerFullWidth
          ? (ISectionLayoutId.BannerFullWidth as never)
          : (camelToSnakeCase(dataPintoSection.pintoSection?.camelCaseLayoutId || '') as never),
      );

      const mappedConfigItems = dataPintoSection.pintoSection?.configItems?.map((item) => ({
        ...item,
        ...(shouldHydrateAllCategoryForConfigItem(item as Record<string, unknown>) ? { categoryId: 0 } : {}),
        ...(item.gteDiscountRatio && { gteDiscountRatio: item.gteDiscountRatio ? item.gteDiscountRatio * 100 : null }),
        ...(!item.gteDiscountRatio && item.lteDiscountRatio && { gteDiscountRatio: 0 }),
        ...(item.lteDiscountRatio && { lteDiscountRatio: item.lteDiscountRatio ? item.lteDiscountRatio * 100 : null }),
        ...(typeof item.hasDiscountEnded === 'boolean' && { hasDiscountEnded: stringifyNullableBoolean(item.hasDiscountEnded) }),
      }));
      setValue(SECTION_BUILDER_FIELD_KEYS.CONFIG_ITEMS, mappedConfigItems as never);
      setValue(SECTION_BUILDER_FIELD_KEYS.NOTE, dataPintoSection.pintoSection?.note as never);

      if (dataPintoSection.pintoSection?.camelCaseLayoutId === SectionLayoutId.MarqueeAnnouncement) {
        setValue(SECTION_BUILDER_FIELD_KEYS.ANNOUNCEMENT_TEXT, dataPintoSection.pintoSection?.configItems?.[0]?.title as never);
        setValue(SECTION_BUILDER_FIELD_KEYS.TEXT_LIGHT_COLOR_HEXCODE, dataPintoSection.pintoSection?.configItems?.[0]?.textLightColorHexcode as never);
        setValue(SECTION_BUILDER_FIELD_KEYS.TEXT_DARK_COLOR_HEXCODE, dataPintoSection.pintoSection?.configItems?.[0]?.textDarkColorHexcode as never);
        setValue(SECTION_BUILDER_FIELD_KEYS.BG_LIGHT_COLOR_HEXCODE1, dataPintoSection.pintoSection?.configItems?.[0]?.bgLightColorHexcode1 as never);
        setValue(SECTION_BUILDER_FIELD_KEYS.BG_DARK_COLOR_HEXCODE1, dataPintoSection.pintoSection?.configItems?.[0]?.bgDarkColorHexcode1 as never);
        setValue(SECTION_BUILDER_FIELD_KEYS.BG_LIGHT_COLOR_HEXCODE2, dataPintoSection.pintoSection?.configItems?.[0]?.bgLightColorHexcode2 as never);
        setValue(SECTION_BUILDER_FIELD_KEYS.BG_DARK_COLOR_HEXCODE2, dataPintoSection.pintoSection?.configItems?.[0]?.bgDarkColorHexcode2 as never);
        setValue(
          SECTION_BUILDER_FIELD_KEYS.GRADIENT_MODE,
          (dataPintoSection.pintoSection?.configItems?.[0]?.bgLightColorHexcode1 !== dataPintoSection.pintoSection?.configItems?.[0]?.bgLightColorHexcode2) as never,
        );
        // convert deeplink back to state for editing purpose by using parseDeepLinkToShortcut
        const data = dataPintoSection.pintoSection?.configItems?.[0] ?? null;

        if (data && data.type !== 'pinto-no-action') {
          setValue(SECTION_BUILDER_FIELD_KEYS.TARGET, {
            type: ISectionTargetType.DEEP_LINK,
          } as never);

          let configItems = [];
          configItems = [
            {
              ...data,
              ...(data?.gteDiscountRatio && { gteDiscountRatio: data?.gteDiscountRatio ? data.gteDiscountRatio * 100 : null }),
              ...(data?.lteDiscountRatio && { lteDiscountRatio: data?.lteDiscountRatio ? data.lteDiscountRatio * 100 : null }),
              ...(typeof data?.hasDiscountEnded === 'boolean' && { hasDiscountEnded: stringifyNullableBoolean(data.hasDiscountEnded) }),
              type: PINTO_SHORTCUT_TYPE_MAP_REVERSE[data.type],
            },
          ];
          setValue(SECTION_BUILDER_FIELD_KEYS.CONFIG_ITEMS, configItems as never);
        }
      }

      if (dataPintoSection.pintoSection?.camelCaseLayoutId === SectionLayoutId.BookCoverWithBanner) {
        const bookCoverWithBannerSection = dataPintoSection.pintoSection as typeof dataPintoSection.pintoSection & {
          imageUrl?: string;
          dayBgImageUrl?: string;
          darkBgImageUrl?: string;
        };

        setValue(SECTION_BUILDER_FIELD_KEYS.BOOK_COVER_WITH_BANNER_IMAGE_URL, bookCoverWithBannerSection?.imageUrl as never);
        setValue(SECTION_BUILDER_FIELD_KEYS.BOOK_COVER_WITH_BANNER_DAY_BG_IMAGE_URL, bookCoverWithBannerSection?.dayBgImageUrl as never);
        setValue(SECTION_BUILDER_FIELD_KEYS.BOOK_COVER_WITH_BANNER_DARK_BG_IMAGE_URL, bookCoverWithBannerSection?.darkBgImageUrl as never);
      }

      if (dataPintoSection.pintoSection?.camelCaseLayoutId === SectionLayoutId.BannerFullWidth) {
        const data = dataPintoSection.pintoSection?.configItems?.[0] ?? null;
        setValue(SECTION_BUILDER_FIELD_KEYS.BANNER_FULL_WIDTH_IMAGE_URL, data?.imageUrl as never);

        if (data) {
          setValue(SECTION_BUILDER_FIELD_KEYS.CONFIG_ITEMS, [{ ...data, type: PINTO_SHORTCUT_TYPE_MAP_REVERSE[data.type] || data.type }] as never);
        }

        if (data && data.type !== 'pinto-no-action') {
          setValue(SECTION_BUILDER_FIELD_KEYS.TARGET, {
            type: ISectionTargetType.DEEP_LINK,
          } as never);

          let configItems = [];
          configItems = [
            {
              ...data,
              ...(data?.gteDiscountRatio && { gteDiscountRatio: data?.gteDiscountRatio ? data.gteDiscountRatio * 100 : null }),
              ...(data?.lteDiscountRatio && { lteDiscountRatio: data?.lteDiscountRatio ? data.lteDiscountRatio * 100 : null }),
              ...(typeof data?.hasDiscountEnded === 'boolean' && { hasDiscountEnded: stringifyNullableBoolean(data.hasDiscountEnded) }),
              type: PINTO_SHORTCUT_TYPE_MAP_REVERSE[data.type],
            },
          ];
          setValue(SECTION_BUILDER_FIELD_KEYS.CONFIG_ITEMS, configItems as never);
        }
      }
    }
  }, [dataPintoSection, setValue]);

  const mapConfigItems = useCallback(() => {
    const configItemsInput = configItems?.map((configItem: IPropsConfigItem) =>
      onConvertDataToConfigItems(configItem, allCategories?.pintoCategories.filter((category) => category !== null) as PintoCategory[]),
    );
    const deeplinkConfigItem = target?.type === ISectionTargetType.DEEP_LINK && configItemsInput?.[0] ? configItemsInput?.[0] : {};
    const deeplinkType = target?.type === ISectionTargetType.DEEP_LINK ? PINTO_SHORTCUT_TYPE_MAP[configItems?.[0]?.type] || 'pinto-no-action' : 'pinto-no-action';

    if (layoutId === ISectionLayoutId.MarqueeAnnouncement) {
      return [
        {
          textLightColorHexcode,
          textDarkColorHexcode,
          bgLightColorHexcode1,
          bgLightColorHexcode2: gradientMode ? bgLightColorHexcode2 : bgLightColorHexcode1,
          bgDarkColorHexcode1,
          bgDarkColorHexcode2: gradientMode ? bgDarkColorHexcode2 : bgDarkColorHexcode1,
          ...deeplinkConfigItem,
          title: announcementText, // Move title AFTER the spread to ensure it overrides any title from deep link config
          type: deeplinkType,
        },
      ];
    }

    if (layoutId === ISectionLayoutId.BannerFullWidth) {
      return [
        {
          imageUrl: bannerFullWidthImageUrl,
          ...deeplinkConfigItem,
          type: deeplinkType,
        },
      ];
    }

    return configItems;
  }, [
    allCategories?.pintoCategories,
    announcementText,
    bgDarkColorHexcode1,
    bgDarkColorHexcode2,
    bgLightColorHexcode1,
    bgLightColorHexcode2,
    configItems,
    gradientMode,
    layoutId,
    bannerFullWidthImageUrl,
    target?.type,
    textDarkColorHexcode,
    textLightColorHexcode,
  ]);

  const onConvertTargetData = useCallback(() => {
    if (!target) return null;
    switch (target.type) {
      case ISectionTargetType.SEARCH:
        return {
          type: target.type,
          searchType: target.searchType || 'ebook',
          searchText: target.searchText || '',
          searchField: target.searchField || null,
          categoryId: normalizeCategoryId(target.categoryId),
          sortBy: target.sortBy || null,
          sortType: target.sortType || null,
          gteRating: target.gteRating ? Number(target.gteRating) : null,
          anyPriceTypes: target.anyPriceTypes || [],
          anyTypes: target.anyTypes || [],
          anyWritingTypes: target.anyWritingTypes || [],
        };
      case ISectionTargetType.EXPLORE:
        return {
          type: target.type,
          exploreType: target.exploreType || EXPLORE_TYPE_VALUE.Trending,
          categoryId: normalizeCategoryId(target.categoryId),
          sortBy: target.sortBy || null,
          sortType: target.sortType || null,
          gteRating: target.gteRating ? Number(target.gteRating) : null,
          anyPriceTypes: target.anyPriceTypes || [],
          anyTypes: target.anyTypes || [],
          anyFileTypes: target.anyFileTypes || [],
          anyWritingTypes: target.anyWritingTypes || [],
        };
      case ISectionTargetType.EXPLORE_BUNDLES:
        return {
          type: target.type,
          exploreType: target.exploreType || SectionTargetExploreEbookBundleType.BestSeller,
          categoryId: normalizeCategoryId(target.categoryId),
          sortBy: target.sortBy || null,
          sortType: target.sortType || null,
          hasDiscount: (target.anyPriceTypes || []).includes('onSale'),
        };
      default:
        return target;
    }
  }, [target]);

  const onConvertDataInput = useCallback(() => {
    const configItemsInput = configItems?.map((configItem: IPropsConfigItem) =>
      onConvertDataToConfigItems(configItem, allCategories?.pintoCategories.filter((category) => category !== null) as PintoCategory[]),
    );

    let targetData = onConvertTargetData();
    targetData = targetData ? omitBy(targetData, isNil) : targetData;

    if (targetData && targetData.type && [ISectionTargetType.SEARCH, ISectionTargetType.EXPLORE, ISectionTargetType.EXPLORE_BUNDLES].includes(targetData.type)) {
      if (contentType === ContentType.Comic) {
        targetData.anyWritingTypes = targetData.anyWritingTypes || [];
      } else {
        delete targetData.anyWritingTypes;
      }
    }

    // If targetData exists but no valid type, remove it
    if (targetData && !targetData.type) {
      targetData = undefined;
    }

    // custom input mapper for marquee announcement
    if (layoutId === ISectionLayoutId.MarqueeAnnouncement) {
      return {
        target: undefined,
        ...omitBy(
          {
            title: announcementText,
            pref: FeedPref.Default,
            productType: ProductType.Ebook,
            layoutId,
            configItems: mapConfigItems(),
            note: isEmpty(note) ? null : note,
          },
          isNil,
        ),
      };
    }

    if (layoutId === ISectionLayoutId.BannerFullWidth) {
      return {
        target: undefined,
        ...omitBy(
          {
            pref: FeedPref.Default,
            productType: ProductType.Ebook,
            layoutId,
            configItems: mapConfigItems(),
            note: isEmpty(note) ? null : note,
          },
          isNil,
        ),
      };
    }

    if (layoutId === ISectionLayoutId.BookCoverWithBanner) {
      return {
        target: targetData,
        ...omitBy(
          {
            title,
            pref: FeedPref.Default,
            productType: ProductType.Ebook,
            layoutId,
            configItems: configItemsInput,
            imageUrl: sectionImageUrl,
            dayBgImageUrl,
            darkBgImageUrl,
            note: isEmpty(note) ? null : note,
          },
          isNil,
        ),
      };
    }

    return {
      target: targetData,
      ...omitBy(
        {
          title,
          pref: FeedPref.Default,
          productType: ProductType.Ebook,
          layoutId,
          configItems: configItemsInput,
          note: isEmpty(note) ? null : note,
        },
        isNil,
      ),
    };
  }, [
    configItems,
    onConvertTargetData,
    layoutId,
    title,
    note,
    allCategories?.pintoCategories,
    contentType,
    announcementText,
    mapConfigItems,
    sectionImageUrl,
    dayBgImageUrl,
    darkBgImageUrl,
  ]);

  const validateBeforeSubmit = useCallback((): boolean => {
    // Max-shortcut-items check
    if (layoutId === ISectionLayoutId.Shortcut && (configItems?.length || 0) > MAX_SHORTCUT_ITEMS) {
      setErrorMessages([`Shortcut layout อนุญาตสูงสุด ${MAX_SHORTCUT_ITEMS} items เท่านั้น`]);
      setShowErrorModal(true);
      return false;
    }

    // Link/LinkShortcut/PintoCustomLink URL check
    const linkItems = configItems?.filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ci: any) => ci.type === IConfigItemType.Link || ci.type === IConfigItemType.LinkShortcut || ci.type === IConfigItemType.PintoCustomLink,
    ) as Array<{ url?: string; link?: string; type: IConfigItemType }>;

    for (const item of linkItems || []) {
      // Support both 'url' and 'link' properties for different item types
      const urlValue = item.url || item.link;

      // IConfigItemType.Link can have empty URL, but others cannot
      if (item.type !== IConfigItemType.Link && !urlValue) {
        setErrorMessages([`Missing URL on link item`]);
        setShowErrorModal(true);
        return false;
      }

      // If URL is provided, validate its format
      if (urlValue) {
        try {
          new URL(urlValue);
        } catch {
          setErrorMessages([`Invalid Link URL format: "${urlValue}"`]);
          setShowErrorModal(true);
          return false;
        }
      }
    }

    if (layoutId === ISectionLayoutId.Shortcut) {
      // Pair each configItem with its index
      interface CollectionItem {
        ci: IPropsConfigItem;
        idx: number;
      }

      const collections: CollectionItem[] = (configItems || [])
        .map((ci: IPropsConfigItem, idx: number) => ({ ci, idx }))
        .filter(({ ci }: { ci: IPropsConfigItem }) => ci.type === IConfigItemType.ExploreEbookCollectionShortcut);

      for (const { ci, idx } of collections) {
        // grab only the filled-in IDs for this one section
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ids = (((ci as any).whitelistUserIds as string[] | undefined) ?? []).filter(Boolean);
        // look for first duplicate
        const dup = ids.find((id, i) => ids.indexOf(id) !== i);
        if (dup) {
          setErrorMessages([`Section #${idx + 1} พบ userId ซ้ำกัน: ${dup}`]);
          setShowErrorModal(true);
          return false;
        }
      }
    }

    if (layoutId === ISectionLayoutId.MarqueeAnnouncement) {
      const errorList: string[] = [];

      // helper: valid 6-digit hex
      const isHex = (val?: string) => /^#([0-9A-Fa-f]{6})$/.test(val ?? '');

      if (!announcementText?.trim()) errorList.push('กรุณากรอก Announcement Text');

      if (!isHex(textLightColorHexcode)) errorList.push('สีข้อความ (โหมดสว่าง) ต้องเป็น Hex code 6 หลัก เช่น #FFFFFF');
      if (!isHex(textDarkColorHexcode)) errorList.push('สีข้อความ (โหมดมืด) ต้องเป็น Hex code 6 หลัก เช่น #FFFFFF');

      if (!isHex(bgLightColorHexcode1)) errorList.push('สีพื้นหลังฝั่งซ้าย/เดี่ยว (โหมดสว่าง) ต้องเป็น Hex code 6 หลัก เช่น #FF0000');
      if (!isHex(bgDarkColorHexcode1)) errorList.push('สีพื้นหลังฝั่งซ้าย/เดี่ยว (โหมดมืด) ต้องเป็น Hex code 6 หลัก เช่น #FF0000');

      if (gradientMode) {
        if (!isHex(bgLightColorHexcode2)) errorList.push('สีพื้นหลังฝั่งขวา (โหมดสว่าง) ต้องเป็น Hex code 6 หลัก เช่น #FF0000');
        if (!isHex(bgDarkColorHexcode2)) errorList.push('สีพื้นหลังฝั่งขวา (โหมดมืด) ต้องเป็น Hex code 6 หลัก เช่น #FF0000');
      }

      if (errorList.length) {
        setErrorMessages(errorList);
        setShowErrorModal(true);
        return false;
      }
    }

    if (layoutId === ISectionLayoutId.BookCoverWithBanner) {
      const errorList: string[] = [];

      if (!sectionImageUrl?.trim()) errorList.push('กรุณากรอก Banner Image URL');
      if (!dayBgImageUrl?.trim()) errorList.push('กรุณากรอก Day Background Image URL');
      if (!darkBgImageUrl?.trim()) errorList.push('กรุณากรอก Dark Background Image URL');

      if (errorList.length) {
        setErrorMessages(errorList);
        setShowErrorModal(true);
        return false;
      }
    }

    if (layoutId === ISectionLayoutId.BannerFullWidth) {
      const errorList: string[] = [];

      if (!bannerFullWidthImageUrl?.trim()) errorList.push('กรุณากรอก Image URL');

      if (errorList.length) {
        setErrorMessages(errorList);
        setShowErrorModal(true);
        return false;
      }
    }

    // everything passed
    return true;
  }, [
    layoutId,
    configItems,
    setErrorMessages,
    setShowErrorModal,
    announcementText,
    bgDarkColorHexcode1,
    bgDarkColorHexcode2,
    bgLightColorHexcode1,
    bgLightColorHexcode2,
    bannerFullWidthImageUrl,
    sectionImageUrl,
    dayBgImageUrl,
    darkBgImageUrl,
    gradientMode,
    textDarkColorHexcode,
    textLightColorHexcode,
  ]);

  const onSubmit = useCallback(async () => {
    if (!validateBeforeSubmit()) return;

    try {
      const id = searchSlug;
      const input = onConvertDataInput();
      console.log('🚀 ~ input:', input);
      let result = false;

      if (id) {
        result = await onMutatePintoUpdateSection(id, input);
      } else {
        result = await onMutatePintoCreateSection(input);
      }

      if (!result) {
        // If your hook returns false on error, you'll still want
        // a more meaningful message here:
        setErrorMessages([`Server rejected the data. Please try again.`]);
        setShowErrorModal(true);
      } else {
        history.push(`/feedAndSection/sections`);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // Now we properly extract the message
      const msg = e.message || JSON.stringify(e, null, 2);
      setErrorMessages([msg]);
      setShowErrorModal(true);
    }
  }, [validateBeforeSubmit, searchSlug, onConvertDataInput, onMutatePintoUpdateSection, history, onMutatePintoCreateSection]);

  return (
    <div>
      {(isLoadingPintoSection || isLoadingMutatePintoCreateSection || isLoadingMutatePintoUpdateSection) && <LoadingModal />}

      {showErrorModal && <ErrorModal onConFirm={() => setShowErrorModal(false)} confirmText="ตกลง" errorMessages={errorMessages} />}

      <div className="px-24 bg-componentsBgGrouped02">
        {/* section: breadcrumbs */}
        <Breadcrumbs items={[{ name: 'Sections', url: '/feedAndSection/sections' }, { name: 'Section Builder' }]} />

        {/* section: title */}
        <div className="flex justify-between pt-5">
          <h1 className="font-dbh text-[28px]">Section Builder</h1>
        </div>

        {/* section: main */}
        <div className="mb-0 divider opacity-80"></div>
      </div>

      <FormProvider {...methods}>
        <div className="flex flex-row w-full space-x-12">
          <div className="w-full space-y-2">
            <SectionNotePresenter />
            <SelectLayoutSectionPresenter
              isEnableChangeLayout={!configItems?.length || [ISectionLayoutId.MarqueeAnnouncement, ISectionLayoutId.BannerFullWidth].includes(layoutId as ISectionLayoutId)}
              className={classNames('w-[100vw]', { 'cursor-not-allowed': configItems?.length })}
              selectedLayout={layoutId}
              onSelectLayout={(value) => {
                if (layoutId !== value) {
                  remove();
                  setValue('layoutId', value);
                }
                if (![ISectionLayoutId.BookBanner, ISectionLayoutId.SquareCover].includes(value as ISectionLayoutId) && target?.type === ISectionTargetType.PRODUCT_SET) {
                  setValue('target', null);
                }
              }}
              configItemsCount={configItems?.length || 0}
            />
            {layoutId ? (
              [ISectionLayoutId.MarqueeAnnouncement, ISectionLayoutId.BannerFullWidth].includes(layoutId as ISectionLayoutId) ? (
                <SectionDetailWithTargetPresenter />
              ) : (
                <>
                  <SectionDetailPresenter />
                  {layoutId === ISectionLayoutId.BookCoverWithBanner && <BookCoverWithBannerSectionDetailPresenter />}
                  <AddItemInSectionPresenter />
                </>
              )
            ) : null}
          </div>
        </div>
      </FormProvider>

      {layoutId ? (
        <div className="p-24 my-16 space-y-8 bg-componentsBgGrouped02 rounded-[8px]">
          <div className="text-systemGrays01LabelPrimary">JSON VIEWER</div>
          <div className="p-8 bg-white rounded-[8px]">
            <ReactJson src={onConvertDataInput()} displayDataTypes={false} />
          </div>
        </div>
      ) : null}

      <div className="sticky bottom-0 z-40 flex items-center justify-between p-16 mt-24 border bg-componentsBgGrouped02 border-componentsDivider">
        <div
          className={classNames('pt-4 text-12', {
            'text-systemGrays01LabelPrimary': configItems?.length,
            'text-systemGrays03LabelTertiary': !configItems?.length,
            '!text-colorsBrandRed01Primary': layoutId === ISectionLayoutId.MarqueeAnnouncement,
          })}
        >
          {`*หากต้องการเปลี่ยน Section Layout กรุณาลบ ConfigItems เดิมออกทั้งหมดก่อน: (ConfigItems: ${configItems?.length || 0})`}
        </div>
        {/* </div> */}
        <div className="space-x-12 ">
          <Link to="/feedAndSection/sections">
            <button className="btn btn-outline">CANCEL</button>
          </Link>
          <button className="btn btn-error" disabled={isEnableSubmitButton} onClick={onSubmit}>
            SAVE
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionBuilderPage;
